// react
import { useState, useEffect, FC, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
// mui
import {
  PopoverOrigin,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  FormLabel,
  Button,
  MenuItem,
  Switch,
  Select,
  useTheme,
  SelectChangeEvent,
  Popover,
  Box,
} from "@mui/material";
import ChevronRight from "../../../images/icons/neutral_edit/Chevron Right.svg";
import { config } from "../../config/config";
import axios from "axios";

// src
import Icon from "../Icon";
import { fetchMode, useThemeMode } from "../../../index";
import { SketchPicker, ColorResult } from "react-color";

import { Viewer } from "../../viewer/viewer";
import Spacer from "../Spacer";
import IOSSwitch from "../IOSSwitch";

// fontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { ProjectDetailContext } from "../../pages/ProjectDetail";

type Props = {
  open: boolean;
  close: () => void;
  userId: string;
  isDetail: boolean;
};

// const SettingDialog: FC<UploadProps> = ({ userInfo }) => {
const SettingDialog: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const { themeMode, setThemeMode } = useThemeMode();
  const [data, setData] = useState({
    projectionDefault: null,
    projectionPoint: null,
    coordinateSetting: null,
    userId: props.userId,
    linkDashboard: null,
    colorTheme: themeMode,
    pointCloudBgcolor: null,
    pointCloudBgalpha: null,
    save: false,
    notifyByEmail: null,
  });
  const [pickerColor, setPickerColor] = useState("#ffffff");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { viewer } = useContext(ProjectDetailContext);

  window.addEventListener("focus", function () {
    if (!openDialog) {
      document.body.style.cssText = "";
    }
  });

  const handleChangeTheme = (e: SelectChangeEvent<string>) => {
    (window as any).webkit?.messageHandlers.callback.postMessage({
      type: "setTheme",
      props: e.target.value,
    });
    setData((prev) => ({
      ...prev,
      colorTheme: e.target.value as string,
      save: false,
    }));
    const newThemeMode = e.target.value as string;
    setThemeMode(newThemeMode);
  };

  // Alpha値を16進数に変換する処理
  const decimalToHex = (alpha: number) =>
    alpha === 0 ? "00" : Math.round(255 * alpha).toString(16);

  const handleChangeComplete = (newColor: ColorResult) => {
    const alpha = newColor.rgb.a;
    setData((prev) => ({
      ...prev,
      pointCloudBgcolor: newColor.hex,
      pointCloudBgalpha: alpha,
      save: false,
    }));
    const hexCode = `${newColor.hex}${decimalToHex(alpha || 0)}`;
    setPickerColor(hexCode);

    if (props.isDetail && viewer !== null) {
      viewer.setBackgroundColor(parseInt(newColor.hex.substring(1), 16), alpha);
    }
  };

  const handlePickerOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setPickerOpen(true);
  };

  const handlePickerClose = () => {
    setPickerOpen(false);
  };

  const handleOpen = async () => {
    const apiUrl = `${config.serverHost}/api/v1/user/setting`;

    const fetchData = async () => {
      try {
        const res = await axios.get(apiUrl, {
          withCredentials: true,
          signal: settingAbortControllerRef.current.signal,
        });

        if (res.data && res.data.pointCloudBgcolor !== null) {
          const hexCode = `${res.data.pointCloudBgcolor}${decimalToHex(
            res.data.pointCloudBgalpha || 0
          )}`;
          setPickerColor(hexCode);
        }

        setData(res.data);
        setThemeMode(res.data.colorTheme);
      } catch (e) {
        console.error(e);
      }
    };

    await fetchData();

    setOpenDialog(true);
    document.body.style.cssText = `position: fixed; top: -${window.scrollY}px; overflow-y: scroll; width: 100%;`;
  };

  const handleClose = () => {
    setData((prev) => ({
      ...prev,
      colorTheme: data.colorTheme as string,
      save: true,
    }));
    console.log("##");
    props.close();
    setOpenDialog(false);
    const scrollY = document.body.style.top;
    document.body.style.cssText = "";
    window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
  };

  const settingAbortControllerRef = useRef(new AbortController());

  useEffect(() => {
    if (!themeMode) {
      setData((prev) => ({ ...prev, colorTheme: "dark", save: false }));
    }
  }, [openDialog]);

  useEffect(() => {
    if (data.save) {
      setData((prev) => ({ ...prev, save: false }));
      const { save, ...postdata } = data;
      const apiUrl = `${config.serverHost}/api/v1/user/setting`;
      settingAbortControllerRef.current.abort();
      settingAbortControllerRef.current = new AbortController();
      axios
        .put(apiUrl, postdata, {
          withCredentials: true,
          signal: settingAbortControllerRef.current.signal,
        })
        .then(() => {})
        .catch(() => {});
    }
  }, [data]);

  useEffect(() => {
    if (props.open) {
      handleOpen();
    }
  }, [props.open]);

  const theme = useTheme();

  const customBackgroundStyles = {
    background: theme.palette.base.modal,
  };

  const customSwitchStyles = {
    color: theme.palette.base.switch,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const RightIcon = () => {
    return (
      <div className="del-pj17">
        <Icon imagePath={ChevronRight} color={customColor.color} />
      </div>
    );
  };

  const buttonStyle = {
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:hover": {
      background: "none",
      boxShadow: "none",
    },
  };

  const circleStyle = {
    backgroundColor: pickerColor,
    borderRadius: "50%",
    width: 35,
    height: 35,
  };

  const borderStyle = {
    border: "2px solid " + customColor.color,
    borderRadius: "50%",
    width: "100%",
    height: "100%",
  };

  type CustomPopoverProps = {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
    content: string;
  };

  const CustomPopover = ({
    anchorOrigin,
    transformOrigin,
    content,
  }: CustomPopoverProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
      <div>
        <IconButton onClick={handleClick} style={{ padding: 0 }}>
          <FontAwesomeIcon icon={faCircleQuestion} style={customColor} />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <Typography
            sx={{ p: 2 }}
            style={{ width: "350px", whiteSpace: "normal" }}
          >
            {content ? content : ""}
          </Typography>
        </Popover>
      </div>
    );
  };

  return (
    <Dialog
      open={openDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{ id: "setting-dialog" }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: customDialogBackgroundColor.color,
          },
        },
      }}
    >
      <DialogTitle className="upload-file16" style={customBackgroundStyles}>
        {t("SETTING")}
        <Button
          style={customColor}
          className="x-btn upload-file15"
          onClick={handleClose}
        >
          ×
        </Button>
        <div className="border-comm" />
      </DialogTitle>
      <DialogContent
        style={{ textAlign: "left", width: "100%", ...customBackgroundStyles }}
      >
        <div
          className="setting-w100 setting-top10"
          style={{ fontWeight: "bold" }}
        >
          {t("GENERAL")}
        </div>
        <div
          className="setting-w100"
          style={{ marginTop: 20, paddingLeft: 15 }}
        >
          {t("SHOW")}
        </div>
        <div className="setting-select">
          <FormLabel style={{ flex: 1, margin: "auto", ...customColor }}>
            {t("APPEARANCE")}
          </FormLabel>
          <Select
            labelId="mode-select-label"
            onChange={handleChangeTheme}
            style={{ height: "36px", padding: "auto" }}
            value={data.colorTheme}
          >
            <MenuItem value={"light" as any}>{t("LIGHT")}</MenuItem>
            <MenuItem value={"dark" as any}>{t("DARK")}</MenuItem>
          </Select>
        </div>
        <div
          className="setting-select"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <p style={{ marginBottom: 0 }}>{t("POINT_CLOUD_BACKGROUND_COLOR")}</p>
          <Button
            variant="contained"
            sx={buttonStyle}
            onClick={handlePickerOpen}
          >
            <Box sx={circleStyle}>
              <Box sx={borderStyle} />
            </Box>
          </Button>
        </div>
        <Popover
          open={pickerOpen}
          anchorEl={anchorEl}
          onClose={handlePickerClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          PaperProps={{
            style: {
              background: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <div style={{ marginLeft: 40 }}>
            <SketchPicker
              color={pickerColor}
              onChangeComplete={handleChangeComplete}
              disableAlpha={false}
            />
          </div>
        </Popover>
        <div className="setting-select1" style={{ marginLeft: 15 }}>
          <div className="setting-text-left">{t("SEND_EMAIL")}</div>
          <div className="setting-text-right">
            <Spacer height={0} width={10} />
            <IOSSwitch
              onChange={(e) => {
                setData((prev) => ({
                  ...prev,
                  notifyByEmail: e.target.checked,
                  save: false,
                }));
              }}
              checked={data.notifyByEmail}
            />
          </div>
        </div>

        <div
          className="setting-w100 setting-top10"
          style={{ fontWeight: "bold" }}
        >
          {"【" + t("UPLOAD_SETTING_ITEMS") + "】"}
        </div>

        <div className="setting-select1" style={{ marginLeft: 20 }}>
          <div className="setting-text-left">{t("ALIGN_SMART_DASHBOARD")}</div>
          <div className="setting-text-right">
            <CustomPopover
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              content={t("DEFAULT_SETTING_EXPLANATION")}
            />
            <Spacer height={0} width={10} />
            <IOSSwitch
              onChange={(e) => {
                if (e.target.checked) {
                  setData((prev) => ({
                    ...prev,
                    coordinateSetting: e.target.checked,
                    save: false,
                  }));
                } else {
                  // 座標系連携がoffならDashboard連携もoff
                  setData((prev) => ({
                    ...prev,
                    coordinateSetting: e.target.checked,
                    linkDashboard: false,
                    save: false,
                  }));
                }
              }}
              checked={data.coordinateSetting}
            />
          </div>
        </div>

        <div className="setting-select1" style={{ marginLeft: 20 }}>
          <div className="setting-text-left">{t("UPLOAD_SYSTEM_SETTING")}</div>
          <div className="setting-text-right">
            <CustomPopover
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              content={t("DEFAULT_SETTING_EXPLANATION")}
            />
            <Spacer height={0} width={10} />
            <IOSSwitch
              onChange={(e) => {
                if (e.target.checked) {
                  // Dashboard連携がonなら座標系連携もon
                  setData((prev) => ({
                    ...prev,
                    coordinateSetting: true,
                    linkDashboard: e.target.checked,
                    save: false,
                  }));
                } else {
                  setData((prev) => ({
                    ...prev,
                    linkDashboard: e.target.checked,
                    save: false,
                  }));
                }
              }}
              checked={data.linkDashboard}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
    // </div >
  );
};

export default SettingDialog;
